import React, { useEffect, useState } from 'react';
import { aig_audit_evidence_library_breakdown, aig_audit_evidence_download } from '../services';
import EvidencePreviewModal, { isPreviewable } from './EvidencePreviewModal';
import AuditPagination from '../Components/AuditPagination';
import {
    STATUS_COLORS, CATEGORY_COLORS, fmtDate, fmtSize, prettyLabel,
    saveBlob, pillStyle, tileStyle, tileLabelStyle, tileValueStyle,
} from './evidenceFormat';

// Drill-down for a single audit: every control that has evidence, the
// auditee's response against it, and the files filed under it.
// Backed by /audit/evidence-library/audits/<id>/breakdown/.
//
// Props:
//   auditId  — audit to open
//   onBack() — return to the audit list
//   flash(msg)

const RESPONSE_FIELDS = [
    ['Non-conformance', 'non_conformance'],
    ['Correction', 'correction'],
    ['Root cause', 'root_cause'],
    ['Corrective action', 'corrective_action'],
    ['Auditee response', 'response_text'],
    ['Auditor assessment', 'auditor_assessment'],
    ['Planned closure', 'planned_closure'],
];

const CONTROL_PAGE_SIZE = 10;

const sectionLabel = {
    fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
    color: 'var(--ap-text-3)', marginBottom: 8,
};

export default function EvidenceAuditDetail({ auditId, onBack, flash }) {
    const [audit, setAudit] = useState(null);
    const [controls, setControls] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [openControls, setOpenControls] = useState({});
    const [previewFile, setPreviewFile] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
    const [page, setPage] = useState(1);
    const [evidenceState, setEvidenceState] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await aig_audit_evidence_library_breakdown(auditId, {
                    page, page_size: CONTROL_PAGE_SIZE,
                    ...(evidenceState ? { evidence_state: evidenceState } : {}),
                });
                const d = r?.statusCode?.response_data?.data;
                if (cancelled) return;
                setAudit(d?.audit || null);
                const groups = Array.isArray(d?.controls) ? d.controls : [];
                setControls(groups);
                setSummary(d?.summary || {});
                setPagination(d?.pagination || { page: 1, total_pages: 1, total_count: 0 });
                // Open the first control so the drill-down never lands on a wall of collapsed rows.
                setOpenControls(groups.length ? { [groups[0].response_id ?? 'unlinked']: true } : {});
            } catch (e) {
                if (!cancelled) {
                    setAudit(null); setControls([]); setSummary({});
                    setPagination({ page: 1, total_pages: 1, total_count: 0 });
                }
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [auditId, page, evidenceState]);

    const toggleControl = (key) => setOpenControls(prev => ({ ...prev, [key]: !prev[key] }));

    const downloadFile = async (file) => {
        try {
            const r = await aig_audit_evidence_download(file.id);
            saveBlob(r?.statusCode || r?.data || r, file.file_name);
        } catch (e) { flash?.('Could not download that file.'); }
    };

    if (loading) {
        return <div className="ap-panel"><div style={{ padding: 40, textAlign: 'center', color: 'var(--ap-text-3)', fontSize: 12 }}>Loading audit evidence…</div></div>;
    }

    return (
        <div className="ap-panel">
            <button className="ap-btn ap-btn--ghost" onClick={onBack} style={{ marginBottom: 14 }}>← All audits</button>

            <div style={{ marginBottom: 16 }}>
                <h3 className="ap-h3" style={{ margin: 0 }}>{audit?.audit_number} — {audit?.title}</h3>
                <div style={{ fontSize: 11, color: 'var(--ap-text-3)', marginTop: 4 }}>
                    {[audit?.framework, audit?.business_unit, prettyLabel(audit?.current_stage)].filter(Boolean).join(' · ')}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                    ['Controls', summary.control_count ?? 0],
                    ['With evidence', summary.controls_with_evidence ?? 0],
                    ['Missing evidence', summary.controls_missing_evidence ?? 0],
                    ['Evidence', summary.file_count ?? 0],
                ].map(([label, value]) => (
                    <div key={label} style={tileStyle}>
                        <div style={tileLabelStyle}>{label}</div>
                        <div style={tileValueStyle}>{value}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {[['', 'All controls'], ['with', 'With evidence'], ['missing', 'Missing evidence']].map(([value, label]) => (
                    <button key={value || 'all'}
                        className={`ap-btn ${evidenceState === value ? 'ap-btn--primary' : 'ap-btn--ghost'}`}
                        onClick={() => { setEvidenceState(value); setPage(1); }}>{label}</button>
                ))}
            </div>

            {!controls.length ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--ap-text-3)', fontSize: 12 }}>
                    {evidenceState === 'missing'
                        ? 'Every control on this audit has evidence.'
                        : evidenceState === 'with'
                            ? 'No evidence uploaded on this audit yet.'
                            : 'This audit has no controls.'}
                </div>
            ) : controls.map(group => {
                const key = group.response_id ?? 'unlinked';
                const isOpen = !!openControls[key];
                const filled = RESPONSE_FIELDS.filter(([, field]) => group.response?.[field]);

                return (
                    <div key={key} style={{ border: '1px solid var(--ap-border)', borderRadius: 6, marginBottom: 10, overflow: 'hidden' }}>
                        <div onClick={() => toggleControl(key)} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                            background: 'var(--ap-surface-2)', cursor: 'pointer', flexWrap: 'wrap',
                        }}>
                            <span style={{ fontSize: 10, color: 'var(--ap-text-3)', width: 10 }}>{isOpen ? '▾' : '▸'}</span>
                            <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ap-text)', minWidth: 70 }}>
                                {group.control?.control_ref || '—'}
                            </span>
                            <span style={{ flex: '1 1 220px', fontSize: 11, color: 'var(--ap-text-2)' }}>
                                {group.control?.title}
                            </span>
                            {group.response?.compliance_status && (
                                <span style={pillStyle(STATUS_COLORS[group.response.compliance_status])}>
                                    {prettyLabel(group.response.compliance_status)}
                                </span>
                            )}
                            {group.response?.category && (
                                <span style={pillStyle(CATEGORY_COLORS[group.response.category])}>
                                    {prettyLabel(group.response.category)}
                                </span>
                            )}
                            {group.file_count === 0 && group.evidence_missing && (
                                <span style={pillStyle('#d97706')}>no evidence</span>
                            )}
                            <span style={{ fontSize: 10, color: 'var(--ap-text-3)', whiteSpace: 'nowrap' }}>
                                {group.file_count} {group.file_count === 1 ? 'file' : 'files'}
                            </span>
                        </div>

                        {isOpen && (
                            <div style={{ padding: '12px 14px' }}>
                                <div style={sectionLabel}>Control</div>
                                {group.control?.description && (
                                    <div style={{ fontSize: 11, color: 'var(--ap-text-2)', marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                                        {group.control.description}
                                    </div>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 16 }}>
                                    {[
                                        ['Domain', group.control?.domain],
                                        ['Evidence required', group.control?.evidence_required === null || group.control?.evidence_required === undefined ? null : (group.control.evidence_required ? 'Yes' : 'No')],
                                    ].map(([label, value]) => (
                                        <div key={label}>
                                            <div style={tileLabelStyle}>{label}</div>
                                            <div style={{ fontSize: 11, color: 'var(--ap-text-2)', marginTop: 2 }}>{value || '—'}</div>
                                        </div>
                                    ))}
                                </div>

                                <div style={sectionLabel}>Auditee response</div>
                                {filled.length === 0 ? (
                                    <div style={{ fontSize: 11, color: 'var(--ap-text-3)', marginBottom: 16 }}>
                                        Nothing recorded against this control yet — only evidence has been uploaded.
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 16 }}>
                                        {filled.map(([label, field]) => (
                                            <div key={field}>
                                                <div style={tileLabelStyle}>{label}</div>
                                                <div style={{ fontSize: 11, color: 'var(--ap-text-2)', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                                                    {group.response[field]}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={sectionLabel}>Evidence ({group.file_count})</div>
                                {group.file_count === 0 && (
                                    <div style={{ fontSize: 11, color: group.evidence_missing ? '#d97706' : 'var(--ap-text-3)' }}>
                                        {group.evidence_missing
                                            ? 'No evidence uploaded — this control requires evidence.'
                                            : 'No evidence uploaded.'}
                                    </div>
                                )}
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                    <tbody>
                                        {group.files.map(file => (
                                            <tr key={file.id} style={{ borderTop: '1px solid var(--ap-border)' }}>
                                                <td style={{ padding: '7px 8px' }}>
                                                    <span style={{ fontWeight: 600, color: 'var(--ap-text)' }}>{file.file_name}</span>
                                                    {file.description && file.description !== file.file_name && (
                                                        <div style={{ color: 'var(--ap-text-3)', fontSize: 10, marginTop: 2 }}>{file.description}</div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '7px 8px', color: 'var(--ap-text-3)', whiteSpace: 'nowrap' }}>{file.uploaded_by?.email || '-'}</td>
                                                <td style={{ padding: '7px 8px', color: 'var(--ap-text-3)', whiteSpace: 'nowrap' }}>{fmtDate(file.uploaded_at)}</td>
                                                <td style={{ padding: '7px 8px', color: 'var(--ap-text-3)', whiteSpace: 'nowrap' }}>{fmtSize(file.file_size)}</td>
                                                <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {isPreviewable(file) && (
                                                        <button className="ap-btn ap-btn--ghost" style={{ marginRight: 6 }} onClick={() => setPreviewFile(file)}>View</button>
                                                    )}
                                                    <button className="ap-btn ap-btn--ghost" onClick={() => downloadFile(file)}>Download</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}

            {pagination.total_pages > 1 && (
                <div style={{ marginTop: 12 }}>
                    <AuditPagination
                        page={pagination.page}
                        totalPages={pagination.total_pages}
                        totalCount={pagination.total_count}
                        onPageChange={setPage}
                    />
                </div>
            )}

            {previewFile && (
                <EvidencePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} flash={flash} />
            )}
        </div>
    );
}
